import { AnimationEvent } from '@angular/animations';
import {
  ConfigurableFocusTrap,
  ConfigurableFocusTrapFactory,
  FocusMonitor,
} from '@angular/cdk/a11y';
import {
  BooleanInput,
  NumberInput,
  coerceBooleanProperty,
} from '@angular/cdk/coercion';
import { Platform } from '@angular/cdk/platform';
import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  Optional,
  Output,
} from '@angular/core';
import { Subject } from 'rxjs';

import { DrawerBaseDirective } from '../drawer-base.directive';
import { luiDrawerAnimations } from '../drawer-animations';

/** Drawer and SideNav display modes. */
export type DrawerMode = 'cover' | 'over' | 'push' | 'side';

/** Drawer and SideNav display modes. */
export type DrawerPosition = 'start' | 'end';

@Component({
    selector: 'lui-drawer',
    templateUrl: './drawer.component.html',
    animations: [
        luiDrawerAnimations.transformDrawer,
        luiDrawerAnimations.drawerHeight,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DrawerComponent
  extends DrawerBaseDirective
  implements AfterViewInit, OnDestroy
{
  private _focusTrap!: ConfigurableFocusTrap;

  /** Whether the view of the component has been attached. */
  private _isAttached!: boolean;

  /** Anchor node used to restore the drawer to its initial position. */
  private _anchor!: Comment | null;

  @HostBinding('class') modelClass = 'lui-drawer';

  @HostBinding('class.lui-drawer-end') get endClass() {
    return this.position === 'end';
  }

  @HostBinding('class.lui-drawer-opened') get openedClass() {
    return this.opened;
  }

  @HostBinding('class.lui-drawer-start') get startClass() {
    return this.position === 'start';
  }

  @HostBinding('class.lui-drawer-cover') get coverClass() {
    return this.mode === 'cover';
  }

  @HostBinding('class.lui-drawer-over') get overClass() {
    return this.mode === 'over';
  }

  @HostBinding('class.lui-drawer-side') get sideClass() {
    return this.mode === 'side';
  }

  @HostBinding('class.lui-drawer-push') get pushClass() {
    return this.mode === 'push';
  }

  // Panel props
  @Input() get usePanel(): boolean {
    return this._usePanel;
  }
  set usePanel(value: BooleanInput) {
    this._usePanel = coerceBooleanProperty(value);
    this._panelChanged.next();
  }
  _usePanel = false;

  @HostBinding('class.lui-drawer-with-panel') get panelClass() {
    return this._usePanel;
  }

  readonly _panelChanged = new Subject<void>();

  /** Current state of the sidenav animation. */
  @HostBinding('@transform') get transformAnimation() {
    return { value: this._animationState, params: {} };
  }

  /** The side that the drawer is attached to. */
  @Input()
  get position(): DrawerPosition {
    return this._position;
  }
  set position(value: DrawerPosition) {
    if (value !== this._position) {
      // Static inputs in Ivy are set before the element is in the DOM.
      if (this._isAttached) {
        this._updatePositionInParent(value);
      }

      this._position = value;
      this.positionChanged.emit();
    }
  }
  private _position: DrawerPosition = 'start';

  /** Mode of the drawer; one of 'cover' 'over', 'push' or 'side'. */
  @Input()
  get mode(): DrawerMode {
    return this._mode;
  }
  set mode(value: DrawerMode) {
    this._mode = value;
    // this._updateFocusTrapState();
    this._modeChanged.next();
  }
  private _mode: DrawerMode = 'over';

  @Output() readonly positionChanged = new EventEmitter<void>();

  /** Emits when the component is destroyed. */
  private readonly _destroyed = new Subject<void>();

  /**
   * An observable that emits when the drawer mode changes. This is used by the drawer container to
   * to know when to when the mode changes so it can adapt the margins on the content.
   */
  readonly _modeChanged = new Subject<void>();

  @HostListener('@transform.start', ['$event']) _transformStarted(
    e: AnimationEvent
  ) {
    this._animationStarted.next(e);
  }

  @HostListener('@transform.done', ['$event']) _transformEnded(
    e: AnimationEvent
  ) {
    this._animationEnd.next(e);
  }

  constructor(
    override _elementRef: ElementRef<HTMLElement>,
    private _focusTrapFactory: ConfigurableFocusTrapFactory,
    private _focusMonitor: FocusMonitor,
    @Optional() @Inject(DOCUMENT) override _doc: Document,
    override _platform: Platform
  ) {
    super(_elementRef, _doc, _platform);
  }

  ngAfterViewInit(): void {
    this._isAttached = true;
    // this._focusTrap = this._focusTrapFactory.create(
    //   this._elementRef.nativeElement
    // );
    // this._updateFocusTrapState();
  }

  ngOnDestroy(): void {
    if (this._focusTrap) {
      this._focusTrap.destroy();
    }
    this._anchor?.remove();
    this._anchor = null;
    this._animationStarted.complete();
    this._animationEnd.complete();
    this._modeChanged.complete();
    this._destroyed.next();
    this._destroyed.complete();
  }

  /** Updates the enabled state of the focus trap. */
  private _updateFocusTrapState() {
    if (this._focusTrap) {
      // The focus trap is only enabled when the drawer is open in any mode other than side.
      this._focusTrap.enabled = this.opened && this.mode !== 'side';
    }
  }

  _getWidth(): number {
    return this._elementRef.nativeElement
      ? this._elementRef.nativeElement.offsetWidth || 0
      : 0;
  }

  /**
   * Updates the position of the drawer in the DOM. We need to move the element around ourselves
   * when it's in the `end` position so that it comes after the content and the visual order
   * matches the tab order. We also need to be able to move it back to `start` if the sidenav
   * started off as `end` and was changed to `start`.
   */
  private _updatePositionInParent(newPosition: DrawerPosition) {
    const element = this._elementRef.nativeElement;
    const parent = element.parentNode;
    if (newPosition === 'end') {
      if (!this._anchor && parent) {
        this._anchor = this._doc.createComment('mat-drawer-anchor');
        parent.insertBefore(this._anchor, element);
      }
      if (parent) {
        parent.appendChild(element);
      }
    } else if (this._anchor && this._anchor.parentNode) {
      this._anchor.parentNode.insertBefore(element, this._anchor);
    }
  }
}
